import { useEffect, useState } from "react";
import { toast } from "sonner";
import { FileText, Download, FolderOpen, CalendarDays, MapPin, Lock, Hourglass, Hand, UserCheck, XCircle } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import EventImage from "@/components/EventImage";
import api, { formatApiError } from "@/api";
import { useAuth } from "@/context/AuthContext";
import { COTISATION_LABELS, formatDate, formatDateTime } from "@/constants";

const displayDate = (iso) => {
  if (!iso) return "";
  if (iso.includes("T00:00:00")) return formatDate(iso);
  return formatDateTime(iso);
};

const RSVP_OPTIONS = [
  { id: "aide", label: "Je viens aider", icon: Hand, activeCls: "bg-emerald-600 text-white border-emerald-600 hover:bg-emerald-700" },
  { id: "participe", label: "Je participe (bénéficiaire)", icon: UserCheck, activeCls: "bg-sky-700 text-white border-sky-700 hover:bg-sky-800" },
  { id: "absent", label: "Je ne peux pas être là", icon: XCircle, activeCls: "bg-slate-500 text-white border-slate-500 hover:bg-slate-600" },
];

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

function EmptyState({ text, testId }) {
  return (
    <p className="text-sm text-slate-500 bg-white rounded-xl border border-slate-200 p-6" data-testid={testId}>
      {text}
    </p>
  );
}

export default function MemberSpace() {
  const { user, setUser } = useAuth();
  const [minutes, setMinutes] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [events, setEvents] = useState([]);
  const [rsvps, setRsvps] = useState({});
  const [notes, setNotes] = useState({});
  const [savingRsvp, setSavingRsvp] = useState(null);
  const [forbidden, setForbidden] = useState(false);
  const [renewNumber, setRenewNumber] = useState("");
  const [renewSaving, setRenewSaving] = useState(false);

  const submitRenewal = async () => {
    if (!renewNumber.trim()) return;
    setRenewSaving(true);
    try {
      await api.post("/auth/renew-membership", { membership_number: renewNumber.trim() });
      const r = await api.get("/auth/me");
      setUser(r.data);
      setRenewNumber("");
      toast.success("Adhésion renouvelée pour la campagne en cours. Merci !");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setRenewSaving(false);
    }
  };

  const active = user && (user.membership_active || ["admin", "super_admin"].includes(user.role));

  useEffect(() => {
    if (!active) return;
    const on403 = (err) => { if (err?.response?.status === 403) setForbidden(true); };
    api.get("/member/minutes").then((r) => setMinutes(r.data)).catch(on403);
    api.get("/member/documents").then((r) => setDocuments(r.data)).catch(on403);
    api.get("/member/events").then((r) => setEvents(r.data)).catch(on403);
    api.get("/member/rsvps").then((r) => {
      const m = {};
      const n = {};
      r.data.forEach((x) => { m[x.event_id] = x; n[x.event_id] = x.note || ""; });
      setRsvps(m);
      setNotes(n);
    }).catch(() => {});
  }, [active]);

  const saveRsvp = async (eventId, status) => {
    setSavingRsvp(eventId + status);
    try {
      await api.post("/member/rsvp", { event_id: eventId, status, note: notes[eventId] || "" });
      setRsvps({ ...rsvps, [eventId]: { event_id: eventId, status, note: notes[eventId] || "" } });
      toast.success("Participation enregistrée");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSavingRsvp(null);
    }
  };

  const saveNote = async (eventId) => {
    const current = rsvps[eventId];
    if (!current) return;
    saveRsvp(eventId, current.status);
  };

  if (!active || forbidden) {
    return (
      <div className="py-12 sm:py-16 lg:py-20" data-testid="member-pending-page">
        <div className="max-w-2xl mx-auto px-4 text-center space-y-5">
          <div className="h-14 w-14 rounded-2xl bg-amber-100 flex items-center justify-center mx-auto">
            <Hourglass className="h-7 w-7 text-amber-700" />
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">Adhésion en attente de validation</h1>
          <p className="text-base text-slate-600 leading-relaxed">
            Bonjour {user?.first_name} ! Votre compte est bien créé. Le bureau de l'association doit
            encore valider votre adhésion (cotisation) avant que vous puissiez accéder aux comptes
            rendus et documents internes. Vous serez notifié très vite — merci de votre patience.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="py-12 sm:py-16" data-testid="member-space-page">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8 flex flex-wrap items-center gap-3">
          <div>
            <p className="text-xs font-semibold uppercase tracking-widest text-sky-700 mb-1">Espace adhérent</p>
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900" data-testid="member-welcome">
              Bonjour {user.first_name}
            </h1>
          </div>
          <Badge
            className={user.cotisation_status === "a_jour"
              ? "bg-emerald-100 text-emerald-800 border border-emerald-200 hover:bg-emerald-100"
              : "bg-amber-100 text-amber-800 border border-amber-200 hover:bg-amber-100"}
            data-testid="member-cotisation-badge"
          >
            Cotisation : {COTISATION_LABELS[user.cotisation_status] || user.cotisation_status}
          </Badge>
        </div>

        {user.membership_renewal_required ? (
          <Card className="mb-6 border-amber-300 bg-amber-50" data-testid="membership-renewal-card">
            <CardContent className="p-5 space-y-3">
              <p className="font-semibold text-amber-900">Nouvelle campagne d'adhésion en cours</p>
              <p className="text-sm text-amber-800 leading-relaxed">
                Merci de renseigner votre numéro d'adhérent pour la campagne en cours. Vous le trouverez dans le mail de
                confirmation HelloAsso — c'est la <strong>référence de commande</strong>.
              </p>
              <div className="flex flex-col sm:flex-row gap-2 max-w-md">
                <Input value={renewNumber} onChange={(e) => setRenewNumber(e.target.value)}
                  placeholder="N° d'adhérent (réf. commande HelloAsso)" data-testid="renewal-number-input" />
                <Button onClick={submitRenewal} disabled={renewSaving || !renewNumber.trim()} data-testid="renewal-submit-btn">
                  {renewSaving ? "Envoi…" : "Valider"}
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : (
          <p className="mb-6 text-sm text-slate-500" data-testid="member-number-line">
            Votre n° d'adhérent : <span className="font-semibold text-slate-700">{user.membership_number || "—"}</span>
            {user.membership_campaign ? <span> · Campagne {user.membership_campaign}</span> : null}
          </p>
        )}

        <Tabs defaultValue="minutes">
          <TabsList className="mb-6 flex-wrap h-auto">
            <TabsTrigger value="minutes" data-testid="member-tab-minutes">Comptes rendus</TabsTrigger>
            <TabsTrigger value="documents" data-testid="member-tab-documents">Documents internes</TabsTrigger>
            <TabsTrigger value="events" data-testid="member-tab-events">Événements</TabsTrigger>
          </TabsList>

          <TabsContent value="minutes" className="space-y-4" data-testid="member-minutes-list">
            {minutes.length === 0 ? (
              <EmptyState text="Aucun compte rendu publié pour le moment." testId="member-minutes-empty" />
            ) : (
              minutes.map((m) => (
                <Card key={m.id} className="border-slate-200" data-testid={`minute-card-${m.id}`}>
                  <CardContent className="p-5 flex flex-wrap items-center gap-4">
                    <div className="h-11 w-11 rounded-xl bg-sky-100 flex items-center justify-center shrink-0">
                      <FileText className="h-5 w-5 text-sky-700" />
                    </div>
                    <div className="flex-1 min-w-[200px]">
                      <p className="font-semibold text-slate-800">{m.title}</p>
                      <p className="text-sm text-slate-500">{m.meeting_type} — {formatDate(m.meeting_date)}</p>
                      {m.summary && <p className="text-sm text-slate-600 mt-1 line-clamp-2">{m.summary}</p>}
                    </div>
                    {m.storage_path && (
                      <Button variant="outline" size="sm" asChild data-testid={`minute-download-${m.id}`}>
                        <a href={`${API_BASE}/files/${m.storage_path}`} target="_blank" rel="noopener noreferrer">
                          <Download className="h-4 w-4 mr-2" />{m.original_filename || "Télécharger"}
                        </a>
                      </Button>
                    )}
                  </CardContent>
                </Card>
              ))
            )}
          </TabsContent>

          <TabsContent value="documents" className="space-y-4" data-testid="member-documents-list">
            {documents.length === 0 ? (
              <EmptyState text="Aucun document interne disponible pour le moment." testId="member-documents-empty" />
            ) : (
              documents.map((d) => (
                <Card key={d.id} className="border-slate-200" data-testid={`document-card-${d.id}`}>
                  <CardContent className="p-5 flex flex-wrap items-center gap-4">
                    <div className="h-11 w-11 rounded-xl bg-emerald-100 flex items-center justify-center shrink-0">
                      <FolderOpen className="h-5 w-5 text-emerald-700" />
                    </div>
                    <div className="flex-1 min-w-[200px]">
                      <p className="font-semibold text-slate-800">{d.title}</p>
                      <p className="text-sm text-slate-500">{d.category}</p>
                      {d.description && <p className="text-sm text-slate-600 mt-1 line-clamp-2">{d.description}</p>}
                    </div>
                    <Button variant="outline" size="sm" asChild data-testid={`document-download-${d.id}`}>
                      <a href={`${API_BASE}/files/${d.storage_path}`} target="_blank" rel="noopener noreferrer">
                        <Download className="h-4 w-4 mr-2" />{d.original_filename || "Télécharger"}
                      </a>
                    </Button>
                  </CardContent>
                </Card>
              ))
            )}
          </TabsContent>

          <TabsContent value="events" className="space-y-4" data-testid="member-events-list">
            {events.length === 0 ? (
              <EmptyState text="Aucun événement programmé pour le moment." testId="member-events-empty" />
            ) : (
              events.map((e) => {
                const myRsvp = rsvps[e.id];
                return (
                  <Card key={e.id} className="border-slate-200" data-testid={`member-event-card-${e.id}`}>
                    <CardContent className="p-5 flex gap-4">
                      {e.image_url && (
                        <EventImage src={e.image_url} alt={`Affiche ${e.title}`}
                          className="shrink-0 w-20 sm:w-24 aspect-[3/4] rounded-xl object-cover shadow-sm border border-slate-100"
                          testId={`member-event-image-${e.id}`} />
                      )}
                      <div className="space-y-3 min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <h3 className="font-semibold text-slate-800">{e.title}</h3>
                          {e.members_only && (
                            <Badge className="bg-amber-100 text-amber-800 border border-amber-200 hover:bg-amber-100">
                              <Lock className="h-3 w-3 mr-1" />Adhérents
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-slate-500 flex items-center gap-1">
                          <CalendarDays className="h-3.5 w-3.5" />{displayDate(e.date)}
                          {e.location && <span className="flex items-center gap-1 ml-3"><MapPin className="h-3.5 w-3.5" />{e.location}</span>}
                        </p>
                        {e.description && <p className="text-sm text-slate-600 whitespace-pre-line">{e.description}</p>}
                        <div className="pt-2 border-t border-slate-100 space-y-3">
                          <div className="flex flex-wrap gap-2" data-testid={`rsvp-buttons-${e.id}`}>
                            {RSVP_OPTIONS.map(({ id, label, icon: Icon, activeCls }) => (
                              <Button
                                key={id}
                                variant="outline"
                                size="sm"
                                disabled={savingRsvp === e.id + id}
                                onClick={() => saveRsvp(e.id, id)}
                                data-testid={`rsvp-${e.id}-${id}`}
                                className={myRsvp?.status === id ? activeCls : ""}
                              >
                                <Icon className="h-3.5 w-3.5 mr-1.5" />{label}
                              </Button>
                            ))}
                          </div>
                          {myRsvp && (
                            <div className="flex gap-2">
                              <Input
                                placeholder="Observation (optionnel)…"
                                value={notes[e.id] || ""}
                                onChange={(ev) => setNotes({ ...notes, [e.id]: ev.target.value })}
                                data-testid={`rsvp-note-${e.id}`}
                                className="text-sm"
                              />
                              <Button size="sm" variant="secondary" onClick={() => saveNote(e.id)} data-testid={`rsvp-note-save-${e.id}`}>
                                OK
                              </Button>
                            </div>
                          )}
                          {myRsvp && (
                            <p className="text-xs text-emerald-700" data-testid={`rsvp-status-${e.id}`}>
                              Votre réponse : {RSVP_OPTIONS.find((o) => o.id === myRsvp.status)?.label}
                            </p>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
